import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import memoize from 'lodash.memoize';
import flow from 'lodash.flow';
import curry from 'lodash.curry';
import { cleanBrowser, loadBrowserLookupDataSource } from 'actions/actionCreators/reportsActions';
import {
  getBrowser,
  getBrowserLoading,
  getBrowserLoaded,
  getBrowserDataTotalCount,
  getBrowserData,
  getBrowserDataLoaded,
  getBrowserDataLoading
} from 'selectors/reports';
import * as editableDocument from 'utils/model/editableDocument';
import { isMandatory, foreach } from 'utils/simple-validations';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import BrowserDialogComponent from './BrowserDialogComponent';
import debounce from 'lodash.debounce';
import { IconButton } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { TextField } from '@totalsoft/rocket-ui';

const validationRules = {
  filters: foreach(filter => ({
    value: [!filter.allowNull && isMandatory()]
  }))
};

const addFiltersPrefix = memoize(addPropertyPrefix('filters'));

const initialPager = {
  page: 1,
  pageSize: 10
};

const DEBOUNCE_DELAY = 1000;

export const BrowserContainer = ({
  actions,
  loadBrowser,
  loadBrowserData,
  browser,
  browserLoaded,
  totalCount,
  filters,
  pager,
  browserId,
  onChangeRowsPerPage,
  onChangePage,
  browserDataLoading,
  browserData,
  pushFilters,
  label,
  onChange,
  orderCriterion,
  onChangeOrderCriterion
}) => {
  const [editBrowser, setEditBrowser] = useState(browser);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState('');
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(null);
  const [orderByFieldName, setOrderByFieldName] = React.useState(null);
  const filterChangedDebounced = useRef(debounce(pushFilters, DEBOUNCE_DELAY));

  useEffect(() => {
    if (browserLoaded) {
      loadBrowserData(filters, pager.page, pager.pageSize, browserId, order, orderBy, orderByFieldName, false);
    }
  }, [filters, pager, orderCriterion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (browserLoaded) {
      setEditBrowser(editableDocument.create(browser));
    }
  }, [browserLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (browserLoaded && editBrowser.dirtyInfo && editBrowser.dirtyInfo.isDirty) {
      const { isValid } = editBrowser.validationInfo;
      if (isValid) {
        const { filters } = editBrowser.document;
        filterChangedDebounced.current(filters.map(x => ({ value: x.value, name: x.name })));
      }
    }
  }, [editBrowser, filterChangedDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpenModal = () => {
    loadBrowser(browserId);
    setShowModal(true);
  };

  const onCloseModal = () => {
    setEditBrowser(editableDocument.create(browser)); //reset local browser
    actions.cleanBrowser(browserId);
    setShowModal(false);
  };
  const onClearInput = () => {
    setValue('');
    onChange(null);
  };

  // eslint-disable-next-line no-unused-vars
  const selectItem = (label, id) => _ => {
    onCloseModal();
    setValue(label);
    onChange(id);
  };

  const onRefresh = () => {
    actions.cleanBrowser(browserId);
    onChangePage(0);
    onChangeOrderCriterion(null, null);
  };

  const handlePropertyChange = curry((prop, value) => {
    setEditBrowser(flow(editableDocument.update(prop, value, null), editableDocument.validate(validationRules, true))(editBrowser));
  });

  const loadOptions = filterName => {
    return actions.loadBrowserLookupDataSource(browserId, filterName);
  };

  const handleRequestSort = (property, fieldName) => {
    const isDesc = orderBy !== null && orderBy === property && order === 'asc';
    setOrder(isDesc ? 'desc' : 'asc');
    setOrderBy(property);
    setOrderByFieldName(fieldName);
    onChangeOrderCriterion(isDesc, property, fieldName);
  };

  return (
    <React.Fragment>
      {showModal && browserLoaded ? (
        <BrowserDialogComponent
          onPropertyChange={addFiltersPrefix(handlePropertyChange)}
          browser={editBrowser}
          browserId={browserId}
          browserLoaded={browserLoaded}
          pager={pager}
          onCloseModal={onCloseModal}
          totalCount={totalCount}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          selectItem={selectItem}
          onRefresh={onRefresh}
          browserData={browserData}
          title={label}
          pushFilters={pushFilters}
          browserDataLoading={browserDataLoading}
          loadOptions={loadOptions}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
        />
      ) : null}

      <TextField
        value={value}
        label={label}
        fullWidth
        readOnly
        endAdornment={
          <>
            {value && (
              <IconButton onClick={onClearInput}>
                <Clear />
              </IconButton>
            )}
            <IconButton onClick={onOpenModal}>
              <Search />
            </IconButton>
          </>
        }
      />
    </React.Fragment>
  );
};

function mapStateToProps(state, ownProps) {
  const { pager, filters, browserId, orderCriterion } = ownProps;
  const order = orderCriterion != null ? (orderCriterion.order ? 'desc' : 'asc') : 'asc';
  const orderBy = orderCriterion != null ? orderCriterion.orderByDescending : null;

  const browser = getBrowser(browserId, state) || {};
  const browserLoaded = getBrowserLoaded(browserId, state) || false;
  const browserLoading = getBrowserLoading(browserId, state) || false;

  const totalCount = getBrowserDataTotalCount(browserId, filters, pager.pageSize, order, orderBy, state);
  const browserData = getBrowserData(browserId, pager.page, pager.pageSize, filters, order, orderBy, state);
  const browserDataLoaded = getBrowserDataLoaded(browserId, pager.page, pager.pageSize, filters, order, orderBy, state) || false;
  const browserDataLoading = getBrowserDataLoading(browserId, pager.page, pager.pageSize, filters, order, orderBy, state) || false;

  return {
    browser,
    browserLoaded,
    browserLoading,
    totalCount,
    browserData,
    browserDataLoaded,
    browserDataLoading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ cleanBrowser, loadBrowserLookupDataSource }, dispatch)
  };
}

BrowserContainer.propTypes = {
  browser: PropTypes.object.isRequired,
  browserLoaded: PropTypes.bool.isRequired,
  browserLoading: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  filters: PropTypes.array,
  pager: PropTypes.object.isRequired,
  browserId: PropTypes.string.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  browserDataLoaded: PropTypes.bool.isRequired,
  browserDataLoading: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  browserData: PropTypes.array.isRequired,
  pushFilters: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  loadBrowser: PropTypes.func.isRequired,
  loadBrowserData: PropTypes.func.isRequired,
  orderCriterion: PropTypes.object,
  onChangeOrderCriterion: PropTypes.func.isRequired,
  other: PropTypes.object
};

const BrowserContainerConnected = connect(mapStateToProps, mapDispatchToProps)(BrowserContainer);

export const BrowserContainerWrapper = props => {
  const [pager, setPager] = useState(initialPager);
  const [editFilters, setEditFilters] = useState(null);
  const [orderCriterion, setOrderCriterion] = useState(null);

  const onChangePage = page => {
    setPager(pagerUpdater('page', ++page));
  };

  const onChangeRowsPerPage = pageSize => {
    setPager(pagerUpdater('pageSize', pageSize));
  };

  const pagerUpdater = (prop, value) =>
    prop === 'pageSize'
      ? {
          ...pager,
          [prop]: value,
          page: 1
        }
      : {
          ...pager,
          [prop]: value
        };

  const pushFilters = filters => {
    setEditFilters(filters);
  };

  const onChangeOrderCriterion = (order, orderByDescending, fieldName) => {
    setOrderCriterion({ order, orderByDescending, fieldName });
  };

  return (
    <BrowserContainerConnected
      {...props}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      filters={editFilters}
      pager={pager}
      pushFilters={pushFilters}
      orderCriterion={orderCriterion}
      onChangeOrderCriterion={onChangeOrderCriterion}
    />
  );
};

export default BrowserContainerWrapper;
